import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';

// Context
import { AuthProvider } from './contexts/AuthContext';
import CookieConsent from './components/CookieConsent';
import { ThemeProvider } from './contexts/ThemeContext';

// Components
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import RouteGuard from './components/Auth/RouteGuard';
import PublicRouteGuard from './components/Auth/PublicRouteGuard';
// Pages
import Home from './pages/Home';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import Dashboard from './pages/Dashboard/Dashboard';
import Projects from './pages/Projects/Projects';
import ProjectDetail from './pages/Projects/ProjectDetail';
import CreateProject from './pages/Projects/CreateProject';
import EditProject from './pages/Projects/EditProject';
import MyProjects from './pages/Projects/MyProjects';
import Users from './pages/Users/Users';
import UserProfile from './pages/Users/UserProfile';
import Financing from './pages/Financing/Financing';
import FinancingDetail from './pages/Financing/FinancingDetail';
import CallForProjects from './pages/CallForProjects/CallForProjects';
import CallForProjectDetail from './pages/CallForProjects/CallForProjectDetail';
import CreateCallForProject from './pages/CallForProjects/CreateCallForProject';
import EditCallForProject from './pages/CallForProjects/EditCallForProject';
import MyApplications from './pages/CallForProjects/MyApplications';
import Contacts from './pages/Contacts/Contacts';
import Subscriptions from './pages/Subscriptions/Subscriptions';
import Payment from './pages/Payment/Payment';
import PaymentCallback from './pages/Payment/PaymentCallback';
import Profile from './pages/Profile/Profile';
import Favorites from './pages/Favorites/Favorites';
import Messaging from './pages/Messaging/Messaging';
import Matching from './pages/Matching/Matching';
import Admin from './pages/Admin/Admin';
import SuperAdmin from './pages/SuperAdmin/SuperAdmin';
import NotFound from './pages/NotFound';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <div className="App">
                <CookieConsent />
                <Routes>
                  {/* Public routes */}
                  <Route path="/" element={<Layout />}>
                    <Route index element={<Home />} />
                    <Route path="login" element={<Login />} />
                    <Route path="register" element={<Register />} />
                    <Route path="projects" element={
                      <PublicRouteGuard requiredPage="projects">
                        <Projects />
                      </PublicRouteGuard>
                    } />
                    <Route path="projects/:id" element={
                      <PublicRouteGuard requiredPage="projects">
                        <ProjectDetail />
                      </PublicRouteGuard>
                    } />
                    <Route path="users" element={
                      <PublicRouteGuard requiredPage="users">
                        <Users />
                      </PublicRouteGuard>
                    } />
                    <Route path="users/:id" element={
                      <PublicRouteGuard requiredPage="users">
                        <UserProfile />
                      </PublicRouteGuard>
                    } />
                    <Route path="financing" element={
                      <PublicRouteGuard requiredPage="financing">
                        <Financing />
                      </PublicRouteGuard>
                    } />
                    <Route path="financing/:id" element={
                      <PublicRouteGuard requiredPage="financing">
                        <FinancingDetail />
                      </PublicRouteGuard>
                    } />
                    <Route path="call-for-projects" element={
                      <PublicRouteGuard requiredPage="callForProjects">
                        <CallForProjects />
                      </PublicRouteGuard>
                    } />
                    <Route path="call-for-projects/:id" element={
                      <PublicRouteGuard requiredPage="callForProjects">
                        <CallForProjectDetail />
                      </PublicRouteGuard>
                    } />
                    <Route path="my-applications" element={
                      <ProtectedRoute>
                        <RouteGuard requiredPage="myApplications">
                          <MyApplications />
                        </RouteGuard>
                      </ProtectedRoute>
                    } />
                    <Route path="profile" element={
                      <ProtectedRoute>
                        <RouteGuard requiredPage="profile">
                          <UserProfile />
                        </RouteGuard>
                      </ProtectedRoute>
                    } />
                    <Route path="subscriptions" element={
                      <PublicRouteGuard requiredPage="subscriptions">
                        <Subscriptions />
                      </PublicRouteGuard>
                    } />
                  </Route>

                  {/* Protected routes */}
                  <Route path="/" element={<Layout />}>
                    <Route path="dashboard" element={
                      <ProtectedRoute>
                        <RouteGuard requiredPage="dashboard">
                          <Dashboard />
                        </RouteGuard>
                      </ProtectedRoute>
                    } />
                    <Route path="projects/create" element={
                      <ProtectedRoute>
                        <RouteGuard requiredPage="myProjects">
                          <CreateProject />
                        </RouteGuard>
                      </ProtectedRoute>
                    } />
                    <Route path="projects/:id/edit" element={
                      <ProtectedRoute>
                        <RouteGuard requiredPage="myProjects">
                          <EditProject />
                        </RouteGuard>
                      </ProtectedRoute>
                    } />
                    <Route path="my-projects" element={
                      <ProtectedRoute>
                        <RouteGuard requiredPage="myProjects">
                          <MyProjects />
                        </RouteGuard>
                      </ProtectedRoute>
                    } />
                    <Route path="call-for-projects/create" element={
                      <ProtectedRoute>
                        <RouteGuard requiredPage="myApplications">
                          <CreateCallForProject />
                        </RouteGuard>
                      </ProtectedRoute>
                    } />
                    <Route path="call-for-projects/:id/edit" element={
                      <ProtectedRoute>
                        <RouteGuard requiredPage="myApplications">
                          <EditCallForProject />
                        </RouteGuard>
                      </ProtectedRoute>
                    } />
                    <Route path="favorites" element={
                      <ProtectedRoute>
                        <RouteGuard requiredPage="favorites">
                          <Favorites />
                        </RouteGuard>
                      </ProtectedRoute>
                    } />
                    <Route path="messaging" element={
                      <ProtectedRoute>
                        <RouteGuard requiredPage="messaging">
                          <Messaging />
                        </RouteGuard>
                      </ProtectedRoute>
                    } />
                    <Route path="matching" element={
                      <ProtectedRoute>
                        <RouteGuard requiredPage="matching">
                          <Matching />
                        </RouteGuard>
                      </ProtectedRoute>
                    } />
                    <Route path="admin" element={
                      <ProtectedRoute>
                        <RouteGuard requiredPage="admin" requiredRole="admin">
                          <Admin />
                        </RouteGuard>
                      </ProtectedRoute>
                    } />
                    <Route path="super-admin" element={
                      <ProtectedRoute>
                        <RouteGuard requiredPage="superAdmin" requiredRole="super-admin">
                          <SuperAdmin />
                        </RouteGuard>
                      </ProtectedRoute>
                    } />
                    <Route path="contacts" element={
                      <ProtectedRoute>
                        <RouteGuard requiredPage="contacts">
                          <Contacts />
                        </RouteGuard>
                      </ProtectedRoute>
                    } />
                    <Route path="payment" element={
                      <ProtectedRoute>
                        <RouteGuard requiredPage="payment">
                          <Payment />
                        </RouteGuard>
                      </ProtectedRoute>
                    } />
                    <Route path="payment/callback" element={
                      <ProtectedRoute>
                        <PaymentCallback />
                      </ProtectedRoute>
                    } />
                    <Route path="profile" element={
                      <ProtectedRoute>
                        <RouteGuard requiredPage="profile">
                          <Profile />
                        </RouteGuard>
                      </ProtectedRoute>
                    } />
                  </Route>

                  {/* 404 */}
                  <Route path="*" element={<NotFound />} />
                </Routes>

                {/* Toast notifications */}
                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: '#363636',
                      color: '#fff',
                    },
                    success: {
                      duration: 3000,
                      iconTheme: {
                        primary: '#22c55e',
                        secondary: '#fff',
                      },
                    },
                    error: {
                      duration: 5000,
                      iconTheme: {
                        primary: '#ef4444',
                        secondary: '#fff',
                      },
                    },
                  }}
                />
              </div>
            </Router>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
